
import React, { useState, useEffect } from 'react';
import { LetsWorkTogether } from './components/ui/LetsWorkTogether.tsx';
import { Pricing } from './components/ui/Pricing.tsx';
import { AuthPage } from './components/ui/AuthPage.tsx';
import { AgencyDashboard } from './components/dashboard/AgencyDashboard.tsx';
import { AccessGate } from './components/access/AccessGate.tsx';
import { BuyerView } from './components/os/BuyerView.tsx';
import { BookingPage } from './components/ui/BookingPage.tsx';
import { supabase } from './lib/supabase.ts';

export type UserRole = 'agency_owner'; 

export interface Creator {
  id: string;
  name: string;
  communityName: string;
  logoUrl: string;
  accentColor: string;
  domain?: string;
  welcomeText: string;
  plan: 'Starter' | 'Growth' | 'Enterprise';
}

export interface OSContent {
  id: string;
  creatorId: string;
  name: string;
  type: 'Video' | 'PDF' | 'Text';
  fileName?: string;
  url?: string;
}

export interface Booking {
  id: string;
  name: string;
  email: string;
  date: string;
  status: 'new' | 'reviewed';
  timestamp: number;
}

export default function App() {
  const [view, setView] = useState<'landing' | 'pricing' | 'auth' | 'dashboard' | 'os_portal' | 'booking'>('landing');
  const [activeToken, setActiveToken] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [role, setRole] = useState<UserRole>('agency_owner');
  
  // App state
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [creators, setCreators] = useState<Creator[]>([]);
  const [content, setContent] = useState<OSContent[]>([]);
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);
  const [targetAssetId, setTargetAssetId] = useState<string | null>(null);

  // Fetch Live Data on Mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch Creators (Profiles)
      const { data: creatorData } = await supabase.from('profiles').select('*');
      if (creatorData) {
        setCreators(creatorData.map(c => ({
          id: c.id,
          name: c.full_name || 'Unnamed Client',
          communityName: c.community_name || c.full_name || 'Portal',
          logoUrl: c.logo_url || '',
          accentColor: c.accent_color || '#3B82F6',
          welcomeText: c.welcome_text || 'Welcome',
          plan: c.plan || 'Starter'
        })));
      }

      // Fetch Assets
      const { data: assetData } = await supabase.from('assets').select('*');
      if (assetData) {
        setContent(assetData.map(a => ({
          id: a.id,
          creatorId: a.creator_id,
          name: a.name,
          type: a.type as any,
          fileName: a.file_name,
          url: a.url
        })));
      }

      // Fetch Bookings
      const { data: bookingData } = await supabase.from('bookings').select('*').order('created_at', { ascending: false });
      if (bookingData) {
        setBookings(bookingData);
      }
    };

    fetchData();
  }, []);

  // Handle access tokens from URL
  useEffect(() => {
    const resolveToken = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('access') || params.get('token');
      
      if (token) {
        setActiveToken(token);
        
        // Find the link in Supabase
        const { data: linkData } = await supabase
          .from('links')
          .select('*')
          .eq('token', token)
          .single();

        if (linkData && linkData.status === 'active') {
          setSelectedCreatorId(linkData.creator_id);
          setTargetAssetId(linkData.asset_id);
          setView('os_portal');
        } else if (token === 'demo-token-123') {
          // Fallback for simulation
          setSelectedCreatorId(creators[0]?.id || '1');
          setView('os_portal');
        }
      }
    };

    if (creators.length > 0) {
      resolveToken();
    }
  }, [creators]);

  const handleLogin = (userRole: string) => {
    setRole('agency_owner');
    setView('dashboard');
  };

  const handleLogout = () => {
    setView('landing');
  };

  const handleNewBooking = async (bookingData: Omit<Booking, 'id' | 'status' | 'timestamp'>) => {
    const timestamp = Date.now();
    const { data, error } = await supabase.from('bookings').insert([{
      name: bookingData.name,
      email: bookingData.email,
      date: bookingData.date,
      status: 'new',
      timestamp: timestamp
    }]).select().single();

    if (error) {
      console.error("Booking error:", error.message);
      return;
    }

    if (data) {
      setBookings(prev => [data, ...prev]);
    }
  };

  const selectedCreator = creators.find(c => c.id === selectedCreatorId);
  const filteredContent = content.filter(c => c.creatorId === selectedCreatorId);

  return (
    <main className="w-full min-h-screen bg-background text-foreground transition-colors duration-300">
      {view === 'landing' && (
        <LetsWorkTogether 
          onShowPricing={() => setView('pricing')}
          onShowBooking={() => setView('booking')}
          onPillAction={() => setView('auth')}
          onBookCall={handleNewBooking}
        />
      )}
      {view === 'pricing' && (
        <Pricing 
          onBack={() => setView('landing')} 
          onSelectPlan={() => setView('booking')}
        />
      )}
      {view === 'booking' && (
        <BookingPage 
          onBack={() => setView('landing')} 
          onBookCall={handleNewBooking} 
          onLogin={() => setView('auth')}
        />
      )}
      {view === 'auth' && (
        <AuthPage 
          onBack={() => setView('landing')} 
          onSuccess={handleLogin}
          onSimulateAccessGate={() => {
            setActiveToken('demo-token-123');
            setView('os_portal');
          }}
        />
      )}
      {view === 'dashboard' && (
        <AgencyDashboard 
          role={role} 
          onLogout={handleLogout} 
          creators={creators} 
          setCreators={setCreators}
          content={content}
          setContent={setContent}
          selectedCreatorId={selectedCreatorId}
          setSelectedCreatorId={setSelectedCreatorId}
          bookings={bookings}
          setBookings={setBookings}
        />
      )}
      {view === 'os_portal' && (
        <>
          {!isAuthorized ? (
            <AccessGate 
              onBack={() => {
                setIsAuthorized(false);
                setView('landing');
                window.history.pushState({}, '', window.location.pathname);
              }} 
              onGrantAccess={() => setIsAuthorized(true)} 
              branding={{
                creatorName: selectedCreator?.name || 'Aure Portal',
                logoUrl: selectedCreator?.logoUrl || '',
                accentColor: selectedCreator?.accentColor || '#FFFFFF'
              }}
            />
          ) : (
            <BuyerView 
              onExit={() => {
                setIsAuthorized(false);
                setView('landing');
                window.history.pushState({}, '', window.location.pathname);
              }} 
              branding={{
                creatorName: selectedCreator?.name || 'Aure Portal',
                logoUrl: selectedCreator?.logoUrl || '',
                accentColor: selectedCreator?.accentColor || '#FFFFFF'
              }}
              content={filteredContent}
              initialAssetId={targetAssetId || undefined}
            />
          )}
        </>
      )}
    </main>
  );
}
